/**
 * Barre de notation (design system, BarreDeNotation), en bas de la séance. Au recto, un seul
 * bouton, « Afficher la réponse », à l'endroit exact où apparaîtront les notes ; au verso,
 * 1 (À revoir), 2 (Difficile), 3 (Bien), 4 (Facile), avec la durée avant le prochain passage.
 * Un appui sur une note juste après le retournement est ignoré (double appui involontaire).
 */
import { useRef } from 'react';
import type { Note } from '../../fsrs/planificateur';
import { cx } from '../outils';
import { raccourciAria, Touche } from '../composants/Touche';

/** Délai pendant lequel un appui sur une note, juste après le retournement, est ignoré. */
const GARDE_DOUBLE_APPUI_MS = 400;

export interface DureeDeNotation {
  texte: string;
  source: 'fsrs' | 'etapes';
  aide: string;
}

export const LIBELLES_NOTES: Record<Note, string> = {
  1: '1 (À revoir)',
  2: '2 (Difficile)',
  3: '3 (Bien)',
  4: '4 (Facile)',
};

export interface BarreDeNotationProps {
  face: 'recto' | 'verso';
  durees: Record<Note, DureeDeNotation> | null;
  touches: Record<Note, string>;
  raccourciRetourner?: string;
  noteAppuyee: Note | null;
  desactivee?: boolean;
  onRetourner: () => void;
  onNoter: (note: Note) => void;
}

export function BarreDeNotation({
  face,
  durees,
  touches,
  raccourciRetourner = 'Espace',
  noteAppuyee,
  desactivee = false,
  onRetourner,
  onNoter,
}: BarreDeNotationProps) {
  /** Moment du dernier appui sur « Afficher la réponse » (pas du raccourci clavier). */
  const retournement = useRef(0);

  if (face === 'recto')
    return (
      <footer className="fc-barre-de-notation" data-face="recto">
        <div className="fc-barre-de-notation__notes">
          <button
            type="button"
            className="fc-note fc-note--afficher"
            aria-keyshortcuts={raccourciAria(raccourciRetourner)}
            disabled={desactivee}
            onClick={() => {
              retournement.current = performance.now();
              onRetourner();
            }}
          >
            <span className="fc-note__libelle">Afficher la réponse</span>
            <Touche>{raccourciRetourner}</Touche>
          </button>
        </div>
      </footer>
    );

  return (
    <footer className="fc-barre-de-notation" data-face={face}>
      <div className="fc-barre-de-notation__notes" role="group" aria-label="Noter la carte">
        {([1, 2, 3, 4] as const).map((note) => {
          const duree = durees?.[note];
          const verso = face === 'verso' && duree;
          return (
            <button
              key={note}
              type="button"
              className="fc-note"
              data-source={duree?.source ?? 'etapes'}
              data-appuye={noteAppuyee === note ? '' : undefined}
              aria-keyshortcuts={touches[note]}
              aria-label={
                verso
                  ? `${LIBELLES_NOTES[note]}, ${duree.texte}, ${duree.aide.charAt(0).toLowerCase()}${duree.aide.slice(1)}`
                  : LIBELLES_NOTES[note]
              }
              title={verso ? duree.aide : undefined}
              disabled={desactivee}
              onClick={() => {
                if (performance.now() - retournement.current < GARDE_DOUBLE_APPUI_MS) return;
                onNoter(note);
              }}
            >
              <span className="fc-note__libelle">{LIBELLES_NOTES[note]}</span>
              <span className={cx('fc-note__duree')}>{duree?.texte ?? ' '}</span>
              <Touche>{touches[note]}</Touche>
            </button>
          );
        })}
      </div>
    </footer>
  );
}
